import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Loader2, Droplet, Flame, Wind, Cloud, Bug, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DAMAGE_TYPES = [
  { key: "Water",      icon: Droplet,      color: "blue" },
  { key: "Fire",       icon: Flame,        color: "rose" },
  { key: "Mold",       icon: Bug,          color: "emerald" },
  { key: "Storm",      icon: Cloud,        color: "purple" },
  { key: "Smoke",      icon: Wind,         color: "zinc" },
  { key: "Structural", icon: Building2,    color: "amber" },
  { key: "Other",      icon: AlertTriangle, color: "zinc" },
];

const AREAS = [
  "Living Room", "Kitchen", "Bathroom", "Bedroom", "Basement",
  "Attic", "Garage", "Exterior", "Roof", "Other",
];

export default function Step4DamageType({ workflow, requestId, onAdvance }) {
  const initial = workflow?.step_4 || {};
  const [primary, setPrimary] = useState(initial.primary_damage_type || "");
  const [primaryOther, setPrimaryOther] = useState(initial.primary_damage_type_other || "");
  const [areas, setAreas] = useState(initial.affected_areas || []);
  const [otherAreas, setOtherAreas] = useState(initial.other_areas_text || "");
  const [saving, setSaving] = useState(false);

  const toggleArea = (a) => {
    setAreas(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const submit = async () => {
    if (!primary) return toast.error("Pick a primary damage type");
    if (primary === "Other" && !primaryOther.trim()) return toast.error("Specify damage type");
    if (areas.length === 0) return toast.error("Select at least one affected area");
    if (areas.includes("Other") && !otherAreas.trim()) return toast.error("Specify the 'Other' area");

    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/4/save`, {
        primary_damage_type: primary,
        primary_damage_type_other: primaryOther || null,
        affected_areas: areas,
        other_areas_text: otherAreas || null,
      });
      toast.success("Damage profile saved");
      onAdvance(5);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-4-form">
      <div className="flex items-center gap-2">
        <Home className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 4 · Property Overview & Damage</h2>
      </div>

      <Card><CardContent className="pt-4">
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">Primary Damage Type</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {DAMAGE_TYPES.map(({ key, icon: Icon }) => {
            const selected = primary === key;
            return (
              <Button key={key} type="button"
                      variant={selected ? "default" : "outline"}
                      onClick={() => setPrimary(key)}
                      className={`h-14 flex flex-col gap-1 ${selected ? "bg-zinc-900 text-white" : ""}`}
                      data-testid={`step-4-damage-${key.toLowerCase()}`}>
                <Icon className="w-4 h-4" />
                <span className="text-[11px] font-semibold">{key}</span>
              </Button>
            );
          })}
        </div>
        {primary === "Other" && (
          <Input value={primaryOther} onChange={e => setPrimaryOther(e.target.value)}
                 placeholder="Specify damage type" className="h-11 mt-3"
                 data-testid="step-4-damage-other-text" />
        )}
      </CardContent></Card>

      <Card><CardContent className="pt-4">
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">
          Affected Areas (select all that apply)
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {AREAS.map(a => {
            const sel = areas.includes(a);
            return (
              <Button key={a} type="button"
                      variant={sel ? "default" : "outline"}
                      onClick={() => toggleArea(a)}
                      className={`h-11 ${sel ? "bg-zinc-900 text-white" : ""}`}
                      data-testid={`step-4-area-${a.toLowerCase().replace(/ /g, "-")}`}>
                {a}
              </Button>
            );
          })}
        </div>
        {areas.includes("Other") && (
          <Input value={otherAreas} onChange={e => setOtherAreas(e.target.value)}
                 placeholder="Describe the other area(s)" className="h-11 mt-3"
                 data-testid="step-4-other-areas-text" />
        )}
      </CardContent></Card>

      <Button onClick={submit} disabled={saving}
              className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="step-4-save-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save & Continue"}
      </Button>
    </div>
  );
}
